import * as types from '@vue-storefront/core/modules/cart/store/mutation-types'
import { Logger } from '@vue-storefront/core/lib/logger'
import EventBus from '@vue-storefront/core/compatibility/plugins/event-bus'
import { CartService } from '@vue-storefront/core/data-resolver'
import { createCartItemForUpdate } from '@vue-storefront/core/modules/cart/helpers'

/** Magento 2 REST: validation / cart rule failures (e.g. min qty) typically return HTTP 400 with JSON `{ message: string }`. */
const HTTP_BAD_REQUEST = 400

/** Human-readable text from Magento REST error bodies (`{ message }`, GraphQL-style `errors`, etc.). */
function magentoApiErrorMessage (body: unknown): string {
  if (body == null) return ''
  if (typeof body === 'string') return body.trim()
  if (typeof body !== 'object') return String(body)
  const o = body as Record<string, unknown>
  const m = o.message
  if (typeof m === 'string' && m.trim()) return m.trim()
  if (Array.isArray(m) && m.length) {
    return m.map(String).filter(Boolean).join(' ')
  }
  const errs = o.errors
  if (Array.isArray(errs) && errs.length) {
    const parts = errs.map((e: unknown) => {
      if (e != null && typeof e === 'object' && 'message' in (e as object)) {
        return String((e as { message?: unknown }).message || '')
      }
      return String(e)
    }).filter(Boolean)
    if (parts.length) return parts.join(' ')
  }
  return ''
}

/**
 * Prefer Magento `message` from the response body (especially on 400 Bad Request).
 * Fallbacks when the body is empty or not JSON.
 */
function magentoCartRestErrorMessage (resultCode: number, body: unknown): string {
  const fromBody = magentoApiErrorMessage(body)
  if (fromBody) return fromBody

  if (resultCode === HTTP_BAD_REQUEST) {
    return 'Bad Request'
  }
  if (resultCode >= 400 && resultCode < 500) {
    return `Request failed (${resultCode})`
  }
  if (resultCode >= 500) {
    return `Server error (${resultCode})`
  }
  return `HTTP ${resultCode}`
}

const mergeActions = {
  async updateServerItem ({ getters, dispatch, commit }, { item }): Promise<true | { errorMessage: string }> {
    const cartItem = createCartItemForUpdate(item)
    const event = await CartService.updateItem(getters.getCartToken, cartItem)
    const wasUpdatedSuccessfully = event.resultCode === 200

    const result = event.result

    if (wasUpdatedSuccessfully) {
      dispatch('syncTotals')
    }

    if (!wasUpdatedSuccessfully && (item.server_item_id || item.item_id)) {
      await dispatch('restoreQuantity', { product: item })
      return { errorMessage: magentoCartRestErrorMessage(event.resultCode, result) }
    }
    if (!wasUpdatedSuccessfully) {
      commit(types.CART_DEL_ITEM, { product: item })
      return { errorMessage: magentoCartRestErrorMessage(event.resultCode, result) }
    }

    const serverItem = result
    const product = {
      ...item,
      ...{
        server_item_id: serverItem.item_id,
        server_quote_id: serverItem.quote_id,
        prev_qty: cartItem.qty,
        product_option: serverItem.product_option,
        type_id: serverItem.product_type
      }
    }

    await dispatch('updateItem', { product })
    EventBus.$emit('cart-after-itemchanged', { item: cartItem })

    return true
  },
  async addServerItems ({ dispatch }, { serverItems }) {
    for (const serverItem of serverItems) {
      await dispatch('addServerItem', { serverItem })
    }
  },
  async addServerItem ({ dispatch, commit }, { serverItem }) {
    Logger.info('No client item for' + serverItem.sku, 'cart')()

    const product = await dispatch('getProductVariant', { serverItem })

    if (product) {
      commit(types.CART_ADD_ITEM, {
        product: { ...product }
      })
      Logger.debug('Product variant for given serverItem has not found', 'cart', serverItem)()
    }

    return []
  }
}

export default mergeActions
